import { useMemo, useState } from 'react'
import { GetStaticProps } from 'next'
import Head from 'next/head'
import Link from 'src/components/Link'

import Container from '@mui/material/Container'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

import { post } from 'src/functions/fetch'
import magazineUrl from 'src/functions/magazineUrl'
import config from 'src/config'
import shopInfos from 'template/infos'
import StaticBreadcrumbs from 'src/components/Breadcrumbs/StaticBreadcrumbs'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type { MagazineOverviewData } from 'src/shared/dbTypes/magazine'
import {
  isPublicMagazinesResponse,
  publicMagazineSummary,
} from 'src/shared/apiBackendResponses/magazine'
import {
  createLandingPageImageAiGeneratedMatcher,
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const siteBaseUrl = (config.siteURL || shopInfos.url || '').replace(/\/$/, '')
const breadcrumb: BreadcrumbItem[] = [
  { title: 'Magazin' },
]

const defaultOverview: MagazineOverviewData = {
  title: 'Magazin',
  subtitle: 'Wohnideen, Ratgeber und Inspirationen',
  description: 'Entdecken Sie redaktionelle Themen, Herstellerberichte, FAQ und kuratierte Empfehlungen aus unserem Shop.',
  metaTitle: 'Wohnideen, Möbel-Ratgeber und Interior Magazin',
  metaDescription: 'Entdecken Sie Wohnideen, Möbel-Ratgeber, Herstellerberichte und kuratierte Empfehlungen für Einrichtung, Design und Interior im Magazin.',
  tagTitle: 'Themen entdecken',
}

const modeLabel = {
  magazine: 'Magazin',
  productImages: 'Bildstrecke',
  faq: 'FAQ / Ratgeber',
} as const

const formatMagazineDate = (value?: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
  }).format(date)
}

const sortByDateDesc = (magazines: publicMagazineSummary[]) =>
  [...magazines].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0
    const dateB = b.date ? new Date(b.date).getTime() : 0
    if (dateA !== dateB) return dateB - dateA
    return b.id - a.id
  })

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    page: {
      background: '#f6f2eb',
      color: '#211f1c',
      minHeight: '100vh',
      paddingBottom: theme.spacing(8),
    },
    shell: {
      paddingTop: theme.spacing(3),
    },
    hero: {
      alignItems: 'stretch',
      display: 'grid',
      gap: theme.spacing(5),
      gridTemplateColumns: 'minmax(0, .8fr) minmax(360px, 1.2fr)',
      marginTop: theme.spacing(2),
      padding: theme.spacing(2, 0, 5),
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
      },
    },
    heroText: {
      alignContent: 'center',
      display: 'grid',
      gap: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '& h1': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '5.4rem',
        lineHeight: .9,
        margin: 0,
        overflowWrap: 'break-word',
      },
      '& h2': {
        color: 'rgba(33, 31, 28, .72)',
        fontSize: '1.3rem',
        fontWeight: 700,
        lineHeight: 1.35,
        margin: 0,
      },
      '& p': {
        color: 'rgba(33, 31, 28, .68)',
        fontSize: '1.05rem',
        lineHeight: 1.65,
        margin: 0,
        maxWidth: 680,
      },
      [theme.breakpoints.down('md')]: {
        '& h1': {
          fontSize: '4rem',
        },
      },
      [theme.breakpoints.down('sm')]: {
        '& h1': {
          fontSize: '2.8rem',
        },
        '& h2': {
          fontSize: '1.08rem',
        },
      },
    },
    heroImage: {
      aspectRatio: '16 / 7',
      background: '#e5ded2',
      minHeight: 320,
      overflow: 'hidden',
      position: 'relative',
      '& img': {
        display: 'block',
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
      '& span': {
        alignItems: 'center',
        color: 'rgba(33, 31, 28, .55)',
        display: 'flex',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '2rem',
        height: '100%',
        justifyContent: 'center',
        padding: theme.spacing(3),
        textAlign: 'center',
      },
      [theme.breakpoints.down('sm')]: {
        aspectRatio: '4 / 3',
        minHeight: 0,
      },
    },
    filters: {
      borderBottom: '1px solid rgba(33, 31, 28, .12)',
      borderTop: '1px solid rgba(33, 31, 28, .12)',
      display: 'grid',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(4),
      padding: theme.spacing(2.2, 0),
      '& h2': {
        fontSize: '.95rem',
        margin: 0,
      },
    },
    tagList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    tagButton: {
      background: '#fffdf8',
      border: '1px solid rgba(33, 31, 28, .16)',
      borderRadius: 999,
      color: '#211f1c',
      cursor: 'pointer',
      font: 'inherit',
      fontWeight: 700,
      minHeight: 42,
      padding: theme.spacing(0, 2),
      transition: 'background .16s ease, border-color .16s ease, transform .16s ease',
      '&:hover': {
        borderColor: '#211f1c',
        transform: 'translateY(-1px)',
      },
    },
    tagButtonActive: {
      background: '#211f1c',
      borderColor: '#211f1c',
      color: '#fffdf8',
    },
    cards: {
      display: 'grid',
      gap: theme.spacing(4, 3),
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    card: {
      color: 'inherit',
      display: 'grid',
      gap: theme.spacing(1.5),
      textDecoration: 'none',
      '&:hover $cardImage img': {
        transform: 'scale(1.035)',
      },
      '&:hover $cardTitle': {
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '4px',
      },
    },
    cardImage: {
      aspectRatio: '4 / 3',
      background: '#e5ded2',
      boxShadow: '0 18px 46px rgba(49, 39, 28, .16)',
      display: 'grid',
      overflow: 'hidden',
      placeItems: 'center',
      position: 'relative',
      '& img': {
        display: 'block',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform .22s ease',
        width: '100%',
      },
      '& span': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.55rem',
        padding: theme.spacing(3),
        textAlign: 'center',
      },
    },
    cardBody: {
      display: 'grid',
      gap: theme.spacing(.75),
      minWidth: 0,
    },
    cardMeta: {
      color: '#7c705f',
      display: 'flex',
      flexWrap: 'wrap',
      fontSize: '.82rem',
      fontWeight: 800,
      gap: theme.spacing(.75),
      textTransform: 'uppercase',
      '& span:not(:last-child)::after': {
        content: '"·"',
        marginLeft: theme.spacing(.75),
      },
    },
    cardTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '1.65rem',
      lineHeight: 1.02,
      margin: 0,
    },
    cardCopy: {
      color: 'rgba(33, 31, 28, .68)',
      lineHeight: 1.55,
      margin: 0,
    },
    cardTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(.6),
      paddingTop: theme.spacing(.25),
      '& span': {
        background: 'rgba(33, 31, 28, .06)',
        borderRadius: 999,
        color: 'rgba(33, 31, 28, .7)',
        fontSize: '.78rem',
        fontWeight: 700,
        padding: theme.spacing(.35, .9),
      },
    },
    empty: {
      background: '#fffdf8',
      border: '1px solid rgba(33, 31, 28, .12)',
      color: 'rgba(33, 31, 28, .62)',
      padding: theme.spacing(4),
    },
  })
)

interface Props {
  magazines: publicMagazineSummary[]
  overview: MagazineOverviewData
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const MagazineOverviewPage: React.FC<Props> = ({ magazines, overview, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )
  const [selectedTag, setSelectedTag] = useState('alle')
  const settings = { ...defaultOverview, ...overview }
  const heroUrl = normalizeMagazineImageUrl(settings.heroImage?.src)
  const tags = useMemo(() => {
    const allTags = magazines.flatMap(magazine => magazine.tags || [])
    return Array.from(new Set(allTags.map(tag => tag.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de'))
  }, [magazines])
  const filteredMagazines = useMemo(() => {
    const sorted = sortByDateDesc(magazines)
    if (selectedTag === 'alle') return sorted
    return sorted.filter(magazine => (magazine.tags || []).includes(selectedTag))
  }, [magazines, selectedTag])

  return <div className={classes.page}>
    <Head>
      <title>{settings.metaTitle || settings.title || 'Magazin'}</title>
      <meta
        name='description'
        content={settings.metaDescription || settings.description || 'Wohnideen, Herstellerberichte, Ratgeber und inspirierende Magazin-Ausgaben.'}
      />
      <link rel='canonical' href={`${siteBaseUrl}/magazin`} />
    </Head>

    <Container className={classes.shell} maxWidth='xl'>
      <StaticBreadcrumbs items={breadcrumb} />

      <header className={classes.hero}>
        <div className={classes.heroText}>
          <h1>{settings.title || 'Magazin'}</h1>
          {settings.subtitle && <h2>{settings.subtitle}</h2>}
          {settings.description && <p>{settings.description}</p>}
        </div>
        <div className={classes.heroImage}>
          {heroUrl
            ? <img src={heroUrl} alt={settings.heroImage?.alt || settings.title || 'Magazin'} />
            : <span>{settings.title || 'Magazin'}</span>
          }
          <AiGeneratedBadge
            aiGenerated={settings.heroImage?.aiGenerated || isAiGeneratedLandingImage(settings.heroImage?.src)}
            placement='top-right'
          />
        </div>
      </header>

      {tags.length > 0 &&
        <section className={classes.filters} aria-label='Magazin-Themen'>
          <h2>{settings.tagTitle || 'Themen entdecken'}</h2>
          <div className={classes.tagList}>
            {['alle', ...tags].map(tag =>
              <button
                key={tag}
                type='button'
                className={[
                  classes.tagButton,
                  selectedTag === tag ? classes.tagButtonActive : '',
                ].join(' ')}
                onClick={() => setSelectedTag(tag)}
              >
                {tag === 'alle' ? 'Alle Beiträge' : tag}
              </button>
            )}
          </div>
        </section>
      }

      {filteredMagazines.length > 0
        ? <div className={classes.cards}>
          {filteredMagazines.map(magazine => {
            const coverUrl = normalizeMagazineImageUrl(magazine.coverImage?.src)
            const date = formatMagazineDate(magazine.date)

            return <Link
              key={magazine.id}
              href={magazine.href || magazineUrl(magazine.id, magazine.slug)}
              className={classes.card}
            >
              <span className={classes.cardImage}>
                {coverUrl
                  ? <img src={coverUrl} alt={magazine.coverImage?.alt || magazine.title} />
                  : <span>{magazine.title}</span>
                }
                <AiGeneratedBadge
                  aiGenerated={magazine.coverImage?.aiGenerated || isAiGeneratedLandingImage(magazine.coverImage?.src)}
                  size='compact'
                  placement='top-left'
                />
              </span>
              <span className={classes.cardBody}>
                <span className={classes.cardMeta}>
                  {date && <span>{date}</span>}
                  <span>{modeLabel[magazine.mode || 'magazine']}</span>
                  {magazine.issue && <span>{magazine.issue}</span>}
                </span>
                <h2 className={classes.cardTitle}>{magazine.title}</h2>
                {(magazine.subtitle || magazine.excerpt) &&
                  <p className={classes.cardCopy}>{magazine.subtitle || magazine.excerpt}</p>
                }
                {magazine.tags.length > 0 &&
                  <span className={classes.cardTags}>
                    {magazine.tags.slice(0, 4).map(tag => <span key={tag}>{tag}</span>)}
                  </span>
                }
              </span>
            </Link>
          })}
        </div>
        : <div className={classes.empty}>Für dieses Thema sind aktuell keine Magazine online.</div>
      }
    </Container>
  </div>
}

export default MagazineOverviewPage

export const getStaticProps: GetStaticProps<Props> = async () => {
  const data = await post({
    url: 'magazines',
    body: config.shop,
    local: true,
  })

  const response = isPublicMagazinesResponse(data) ? data : undefined
  const siteSettings = await fetchSiteShopSettings({ local: true }).catch(() => undefined)
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    response?.landingPageImageAiGenerated,
    siteSettings?.landingPageImageAiGenerated,
  )

  return {
    props: {
      magazines: response?.magazines || [],
      overview: response ? { ...defaultOverview, ...response.overview } : defaultOverview,
      ...(landingPageImageAiGenerated
        ? { landingPageImageAiGenerated }
        : {}),
    },
    revalidate: 300,
  }
}
